import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const modelCategories = [
  {
    title: 'Large Language Models',
    icon: 'ri-chat-3-line',
    models: ['GPT-4o', 'Claude 3.5 Sonnet', 'Gemini 1.5 Pro', 'Llama 3.1 405B', 'Mistral Large', 'DeepSeek V3'],
    description: 'Switch between frontier LLMs without leaving your workspace. Shared context, prompt history, and comparisons across models in one pane.',
    bgClass: 'bg-primary-50',
    iconBgClass: 'bg-primary-100 text-primary-700',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20dashboard%20UI%20mockup%20showing%20large%20language%20model%20chat%20interface%20with%20multiple%20conversation%20panels%2C%20GPT%20Claude%20Gemini%20Llama%20model%20selector%20sidebar%2C%20orange%20and%20blue%20accent%20highlights%2C%20warm%20cream%20background%2C%20stylized%20SaaS%20illustration%2C%20professional%20minimal%20aesthetic&width=1200&height=900&seq=omni-feat-llm&orientation=landscape',
  },
  {
    title: 'Image & Vision',
    icon: 'ri-image-edit-line',
    models: ['DALL-E 3', 'Stable Diffusion XL', 'Midjourney API', 'Flux Pro', 'Ideogram'],
    description: 'Generate, edit, and analyze images across multiple engines. Compare outputs side-by-side, batch process with brand guardrails.',
    bgClass: 'bg-accent-50',
    iconBgClass: 'bg-accent-100 text-accent-700',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20UI%20mockup%20showing%20AI%20image%20generation%20platform%20with%20multiple%20generated%20artwork%20previews%20in%20a%20grid%2C%20DALL-E%20Stable%20Diffusion%20Midjourney%20model%20selector%20tabs%2C%20orange%20and%20blue%20accent%20highlights%2C%20warm%20cream%20background%2C%20stylized%20SaaS%20illustration&width=1200&height=900&seq=omni-feat-img&orientation=landscape',
  },
  {
    title: 'Video Generation',
    icon: 'ri-film-line',
    models: ['Runway Gen-3', 'Pika 2.0', 'Kling', 'Sora', 'Luma Dream Machine'],
    description: 'Text-to-video and image-to-video generation from multiple providers. Unified queue, rendering priority controls, and team review workflows.',
    bgClass: 'bg-background-100',
    iconBgClass: 'bg-background-200 text-foreground-700',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20UI%20mockup%20showing%20AI%20video%20generation%20dashboard%20with%20video%20timeline%20editor%20and%20preview%20thumbnails%2C%20Runway%20Pika%20Kling%20model%20selector%2C%20orange%20and%20blue%20accent%20highlights%2C%20warm%20cream%20background%2C%20stylized%20SaaS%20illustration&width=1200&height=900&seq=omni-feat-vid&orientation=landscape',
  },
  {
    title: 'Code & Agents',
    icon: 'ri-code-s-slash-line',
    models: ['Claude Code', 'GitHub Copilot', 'Cursor', 'Devin', 'CrewAI', 'AutoGPT'],
    description: 'Agentic workflows, code generation, and autonomous task execution. Orchestrate multi-agent pipelines with human-in-the-loop approval gates.',
    bgClass: 'bg-secondary-50',
    iconBgClass: 'bg-secondary-100 text-secondary-700',
    imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20UI%20mockup%20showing%20AI%20code%20editor%20and%20agentic%20workflow%20dashboard%20with%20terminal%20panels%20and%20code%20diff%20views%2C%20Claude%20Copilot%20Devin%20agent%20selector%2C%20orange%20and%20blue%20accent%20highlights%2C%20warm%20cream%20background%2C%20stylized%20SaaS%20illustration&width=1200&height=900&seq=omni-feat-code&orientation=landscape',
  },
];

const pricingTiers = [
  {
    name: 'Starter',
    price: '$499',
    period: '/month',
    description: 'For small teams getting started with AI aggregation.',
    features: [
      'Up to 10 team members',
      'Access to 20+ LLMs',
      '1M tokens/month pooled',
      'Basic prompt library',
      'Email support',
      'Shared workspace',
    ],
    highlighted: false,
    buttonText: 'Start free trial',
    buttonClass: 'bg-background-50 hover:bg-background-100 border border-background-300 text-foreground-900',
  },
  {
    name: 'Professional',
    price: '$1,999',
    period: '/month',
    description: 'For growing teams that need governance and advanced models.',
    features: [
      'Up to 50 team members',
      'All 40+ models including image & video',
      '10M tokens/month pooled',
      'Advanced prompt library with versioning',
      'Role-based access control',
      'Audit logs & usage analytics',
      'Priority support',
      'SSO (Google, Azure AD)',
    ],
    highlighted: true,
    buttonText: 'Start free trial',
    buttonClass: 'bg-accent-500 hover:bg-accent-600 text-background-50',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'For large organizations with compliance and data residency needs.',
    features: [
      'Unlimited team members',
      'All models + custom model integration',
      'Unlimited tokens with volume pricing',
      'Dedicated prompt engineer support',
      'Custom RBAC & audit policies',
      'On-prem / VPC deployment option',
      'SLA with 99.95% uptime',
      'Dedicated account manager',
      'Data residency controls',
      'Custom model fine-tuning pipeline',
    ],
    highlighted: false,
    buttonText: 'Contact sales',
    buttonClass: 'bg-background-50 hover:bg-background-100 border border-background-300 text-foreground-900',
  },
];

const faqItems = [
  {
    q: 'Can I bring my own API keys for specific models?',
    a: 'Yes — Omni AI supports BYOK (Bring Your Own Key) mode. You can use your existing OpenAI, Anthropic, or other provider keys alongside our pooled access, and we consolidate all usage into a single dashboard.',
  },
  {
    q: 'How does data privacy work across different model providers?',
    a: 'Omni AI routes each request to the selected provider securely. For Enterprise plans, we offer a proxy layer that anonymizes sensitive data before it reaches external APIs. You can also deploy Omni AI entirely within your VPC for maximum control.',
  },
  {
    q: 'Do you support fine-tuning or custom model hosting?',
    a: 'On the Enterprise plan, we support custom model fine-tuning workflows and can host open-weight models (like Llama or Mistral) on our GDB cloud infrastructure or yours. Contact our solutions team for details.',
  },
  {
    q: 'What kind of analytics and reporting do you provide?',
    a: 'Every plan includes per-model cost breakdowns, token usage trends, team productivity metrics, and quality scoring. Professional and Enterprise plans add exportable reports, anomaly detection, and budget alerting.',
  },
];

export default function OmniAIPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    document.title = 'Omni AI — Multi-Model AI Aggregator Platform | Sky Edge Infotech';
    const metaTags = [
      { attr: 'name', name: 'description', content: 'Omni AI by Sky Edge Infotech aggregates 40+ frontier AI models — LLMs, image, video, code, and agentic — behind a single enterprise workspace with governance, spend controls, and shared context. Built for teams that ship.' },
      { attr: 'name', name: 'keywords', content: 'AI aggregator platform, multi-model AI workspace, enterprise AI, LLM aggregation, Omni AI, Sky Edge Infotech, AI model orchestration, GPT Claude Gemini unified, image video code AI models' },
    ];
    const added: HTMLMetaElement[] = [];
    metaTags.forEach(({ attr, name, content }) => {
      const el = document.createElement('meta');
      el.setAttribute(attr, name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      added.push(el);
    });
    return () => { added.forEach((el) => el.remove()); };
  }, []);

  const handleDemoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('.field-alt-website') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: 'Thank you! We will get back to you shortly.' });
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9ihrcmc26n1c7c5ptkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: 'Demo request received! Our team will reach out within 24 hours.' });
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please email us directly.';
        setFormStatus({ type: 'error', message: msg });
      }
    } catch {
      setFormStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center bg-primary-950 overflow-hidden pt-16">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/60 to-primary-950" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] rounded-full bg-accent-500/10 blur-[120px]" />
        <div className="absolute bottom-10 left-20 w-[400px] h-[400px] rounded-full bg-primary-500/15 blur-[100px]" />

        <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="flex flex-col">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse"></span>
                In-house product
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-background-50 leading-tight">
                One platform.
                <br />
                <span className="text-gradient-brand">Every AI model.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-background-200 leading-relaxed max-w-xl">
                Omni AI aggregates 40+ frontier AI models — LLMs, image, video, code, and agentic — behind a single workspace with enterprise governance, spend controls, and shared context. Built for teams that ship.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#demo-form" className="px-6 py-3.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center">
                  Request a demo <i className="ri-arrow-right-line"></i>
                </a>
                <button
                  onClick={() => setShowChat(!showChat)}
                  className="px-6 py-3.5 rounded-md bg-background-50/8 hover:bg-background-50/15 border border-background-50/20 text-background-100 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  Watch overview (4 min)
                </button>
              </div>

              <div className="mt-10 flex items-center gap-8">
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">40+</span>
                  <span className="text-xs text-background-300">AI models unified</span>
                </div>
                <div className="w-px h-10 bg-background-50/15" />
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">$0.003</span>
                  <span className="text-xs text-background-300">per 1K tokens avg</span>
                </div>
                <div className="w-px h-10 bg-background-50/15" />
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-2xl text-background-50">99.95%</span>
                  <span className="text-xs text-background-300">uptime SLA</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-lg overflow-hidden border border-background-50/15 bg-background-50/5 backdrop-blur">
                <img
                  src="https://readdy.ai/api/search-image?query=Hyper-detailed%20realistic%20AI%20aggregator%20dashboard%20UI%20screenshot%20style%20with%20crisp%20clear%20typography%2C%20a%20unified%20chat%20interface%20comparing%20outputs%20from%20multiple%20AI%20models%20side%20by%20side%20in%20panels%2C%20model%20selector%20sidebar%20with%20colorful%20model%20icons%20for%20GPT%20Claude%20Gemini%20Llama%20Mistral%2C%20orange%20and%20navy%20blue%20accent%20highlights%2C%20token%20usage%20gauge%20widgets%20with%20sharp%20data%20visualization%2C%20clean%20modern%20enterprise%20SaaS%20interface%2C%20bright%20high-contrast%20professional%20screenshot%20with%20sharp%20lines%20and%20readable%20text%2C%20no%20blur%2C%20ultra%20clear&width=1200&height=900&seq=omni-ai-hero-dash-v2&orientation=landscape&nocache=true"
                  alt="Omni AI aggregator platform dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-3 -right-3 hidden lg:flex items-center gap-3 bg-background-50 text-foreground-900 px-4 py-3 rounded-md border border-background-200">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-sparkling-2-line"></i>
                </div>
                <div className="text-xs">
                  <div className="font-semibold">SOC 2 Type II</div>
                  <div className="text-foreground-500">Enterprise-ready security</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by / Integrations strip */}
      <section className="bg-background-100 border-y border-background-200 py-8">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-foreground-400 whitespace-nowrap">Integrated with</span>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {['OpenAI', 'Anthropic', 'Google AI', 'Meta Llama', 'Mistral', 'Stability AI', 'Runway', 'Perplexity'].map((p) => (
              <span key={p} className="text-sm font-medium text-foreground-500 whitespace-nowrap">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Deep-Dive */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-stack-line"></i>
              Feature deep-dive
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">
              Every model category, one interface.
            </h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">
              Omni AI isn't just a chat wrapper — it's a full workspace with team collaboration, governance, analytics, and model orchestration built in from the ground up.
            </p>
          </div>

          <div className="space-y-16">
            {modelCategories.map((cat, idx) => (
              <div key={cat.title} className={`rounded-lg border border-background-200 ${cat.bgClass} overflow-hidden`}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 ${idx % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                  <div className={`p-8 md:p-12 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className={`w-12 h-12 flex items-center justify-center rounded-md ${cat.iconBgClass} mb-5`}>
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">{cat.title}</h3>
                    <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{cat.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {cat.models.map((m) => (
                        <span key={m} className="px-3 py-1.5 rounded-full bg-background-50 border border-background-300 text-xs font-medium text-foreground-700 whitespace-nowrap">{m}</span>
                      ))}
                    </div>
                  </div>
                  <div className={`relative min-h-[320px] ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <img
                      src={cat.imageUrl}
                      alt={`${cat.title} interface preview`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance & Enterprise */}
      <section className="py-20 md:py-28 bg-primary-950 text-background-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern opacity-30" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent-500/5 blur-[100px]" />
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider mb-5 mx-auto">
              <i className="ri-shield-keyhole-line"></i>
              Enterprise governance
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl leading-tight">Built for the enterprise, from day one.</h2>
            <p className="mt-4 text-background-200 leading-relaxed">SOC 2 Type II compliant. Role-based access. Audit trails. And deployment options that fit your compliance posture — cloud, hybrid, or on-prem.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-shield-check-line', title: 'SOC 2 Type II', desc: 'Annual independent audits for security, availability, and confidentiality.' },
              { icon: 'ri-user-settings-line', title: 'RBAC & SSO', desc: 'Granular roles, team workspaces, and SAML/OIDC single sign-on with major IdPs.' },
              { icon: 'ri-file-list-3-line', title: 'Audit Logs', desc: 'Every prompt, model call, and config change is logged with full export capability.' },
              { icon: 'ri-server-line', title: 'Deploy Anywhere', desc: 'Cloud (GDB / AWS / GCP / Azure), hybrid, or fully on-prem within your VPC.' },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-md bg-background-50/5 border border-background-50/10">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500/20 text-accent-300 mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <div className="text-base font-semibold mb-2">{item.title}</div>
                <p className="text-sm text-background-300 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-price-tag-3-line"></i>
              Pricing
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">Plans that scale with your AI adoption.</h2>
            <p className="mt-4 text-base text-foreground-600 leading-relaxed">Start small. Scale fast. All plans include a 14-day free trial with no credit card required.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-lg border overflow-hidden p-8 flex flex-col ${
                  tier.highlighted
                    ? 'border-accent-400 bg-background-50 ring-1 ring-accent-400/50 scale-[1.02]'
                    : 'border-background-200 bg-background-50'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute top-0 left-0 right-0 bg-accent-500 text-background-50 text-xs font-semibold text-center py-1.5 uppercase tracking-wider">
                    Most popular
                  </div>
                )}
                <div className={`${tier.highlighted ? 'mt-6' : ''}`}>
                  <div className="text-xs font-semibold uppercase tracking-widest text-foreground-400 mb-3">{tier.name}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading font-bold text-4xl text-foreground-900">{tier.price}</span>
                    {tier.period && <span className="text-sm text-foreground-500">{tier.period}</span>}
                  </div>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{tier.description}</p>
                </div>

                <ul className="mt-8 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-foreground-700">
                      <span className="mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href="#demo-form"
                  className={`mt-8 px-5 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors text-center block ${tier.buttonClass}`}
                >
                  {tier.buttonText}
                </a>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl mx-auto bg-background-50 border border-background-200 rounded-lg p-6 md:p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <i className="ri-building-4-line text-accent-500 text-xl"></i>
              <span className="font-heading font-bold text-lg text-foreground-900">Need a custom plan?</span>
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Volume pricing, custom model integration, dedicated support, and white-label options available.
              <a href="#demo-form" className="text-accent-600 hover:text-accent-700 font-medium cursor-pointer ml-1">Talk to our sales team.</a>
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <div className="max-w-[900px] mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wider mb-5">
              <i className="ri-question-answer-line"></i>
              FAQ
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl text-foreground-900 leading-tight">Frequently asked questions.</h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <details key={item.q} className="group rounded-md border border-background-200 bg-background-50 overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer flex items-center justify-between gap-4 list-none">
                  <span className="font-medium text-foreground-900 text-sm md:text-base">{item.q}</span>
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:rotate-45 transition-transform flex-shrink-0">
                    <i className="ri-add-line"></i>
                  </span>
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Form */}
      <section id="demo-form" className="py-20 md:py-28 bg-primary-950 text-background-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern opacity-20" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-500/8 blur-[100px]" />
        <div className="relative max-w-[700px] mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/15 border border-accent-400/30 text-accent-300 text-xs font-semibold w-fit uppercase tracking-wider mb-5 mx-auto">
            <i className="ri-rocket-2-line"></i>
            Get started
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl leading-tight">Ready to unify your AI stack?</h2>
          <p className="mt-4 text-background-200 leading-relaxed max-w-lg mx-auto">Fill in the form below and our product team will set up a personalized walkthrough within 24 hours.</p>

          <form
            data-readdy-form
            onSubmit={handleDemoSubmit}
            className="mt-10 bg-background-50/5 border border-background-50/15 backdrop-blur rounded-lg p-6 md:p-8 text-left"
          >
            <input
              type="text"
              name="website_alt"
              className="field-alt-website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="omni-first-name" className="block text-sm font-medium text-background-200 mb-1.5">First name *</label>
                <input
                  id="omni-first-name"
                  name="first_name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="John"
                />
              </div>
              <div>
                <label htmlFor="omni-last-name" className="block text-sm font-medium text-background-200 mb-1.5">Last name *</label>
                <input
                  id="omni-last-name"
                  name="last_name"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="omni-email" className="block text-sm font-medium text-background-200 mb-1.5">Work email *</label>
                <input
                  id="omni-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label htmlFor="omni-company" className="block text-sm font-medium text-background-200 mb-1.5">Company name *</label>
                <input
                  id="omni-company"
                  name="company"
                  type="text"
                  required
                  className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors"
                  placeholder="Acme Inc."
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="omni-team-size" className="block text-sm font-medium text-background-200 mb-1.5">Team size *</label>
              <select
                id="omni-team-size"
                name="team_size"
                required
                className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors appearance-none cursor-pointer"
              >
                <option value="" className="text-foreground-900">Select team size</option>
                <option value="1-10" className="text-foreground-900">1 - 10</option>
                <option value="11-50" className="text-foreground-900">11 - 50</option>
                <option value="51-200" className="text-foreground-900">51 - 200</option>
                <option value="201-1000" className="text-foreground-900">201 - 1,000</option>
                <option value="1000+" className="text-foreground-900">1,000+</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="omni-message" className="block text-sm font-medium text-background-200 mb-1.5">What would you like to explore? (optional)</label>
              <textarea
                id="omni-message"
                name="message"
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-md bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                placeholder="Tell us about your use case, team, and what you're hoping to achieve with Omni AI..."
              ></textarea>
              <p className="text-xs text-background-400 mt-1">Max 500 characters</p>
            </div>

            {formStatus && (
              <div className={`mb-6 p-3 rounded-md text-sm font-medium ${
                formStatus.type === 'success'
                  ? 'bg-accent-500/15 border border-accent-400/30 text-accent-300'
                  : 'bg-red-500/10 border border-red-400/30 text-red-300'
              }`}>
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              className="w-full px-6 py-3.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
            >
              Submit demo request
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}